/** @format */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { Button, cn } from '@clarix/ui';

/**
 * Detects new deployments by fetching /version.json and shows a non-intrusive banner.
 *
 * Triggers (no timer — zero background polling):
 *  1. App mount           → catches stale cached bundles on load
 *  2. Route change        → every internal navigation is a checkpoint
 *  3. Tab becomes visible → instant check when user returns from another tab
 *
 * Optimisations:
 *  - No interval → zero background requests while user is idle
 *  - Stops checking once update is found → no wasted fetches after detection
 *  - cache:'no-store' → bypasses SW + browser cache, always hits the server
 *
 * Flow:
 *  1. Build embeds __BUILD_TIME__ (ms) and emits /version.json with the same value.
 *  2. On each trigger, fetches /version.json with cache:'no-store'.
 *  3. If server buildTime > __BUILD_TIME__ → banner appears — no page refresh needed.
 *  4. User clicks "Update Now" → the boot chain is re-primed from the network and
 *     the page reloads onto the new build (see applyUpdate below — a bare
 *     `location.reload()` is NOT enough).
 */

/**
 * Stop waiting on the network and reload anyway. Re-priming is an optimisation
 * on top of the reload, never a precondition for it: a hung request must not
 * leave the user staring at a button that already said "Updating…".
 */
const PRIME_TIMEOUT_MS = 4_000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Re-fetch the files the next boot depends on with hard-refresh semantics.
 *
 * WHY THIS EXISTS — `window.location.reload()` on its own lands back on the OLD
 * build, so the banner re-fires within a second and the click looks like it did
 * nothing. The boot chain is:
 *
 *   index.html                     served no-store, but byte-identical per build
 *     └─ mf-entry-bootstrap-N.js   FIXED filename, new content every build  ← stale
 *          └─ assets/<name>-<hash>.js   content-hashed, always safe
 *
 * `@module-federation/vite` emits that bootstrap file with a hard-coded name, so
 * only its BODY changes between builds — and a plain reload revalidates just the
 * top-level document while every subresource is served straight from the HTTP
 * cache. The fresh index.html therefore re-imports the CACHED bootstrap and boots
 * the previous build again. Ctrl+Shift+R works because it bypasses the cache for
 * subresources too.
 *
 * So do what the hard refresh does, for the two files that matter. Per the fetch
 * spec, `cache: 'reload'` skips the cache, sends `Cache-Control: no-cache` +
 * `Pragma: no-cache` (so a CDN in front revalidates as well), and WRITES the
 * response back — overwriting the stale entry. The reload that follows then finds
 * the current bootstrap already in cache and boots the new build.
 *
 * The build also stamps the bootstrap URL with the build time
 * (tools/vite/bustMfBootstrapCache.ts), which fixes this for plain refreshes too.
 * This function is what rescues a session already running a build made before
 * that stamp existed — and it costs one request.
 */
async function primeBootChain(): Promise<void> {
  const res = await fetch(window.location.href, { cache: 'reload' });
  if (!res.ok) return;
  const html = await res.text();

  const entries = new Set<string>();
  for (const [tag] of html.matchAll(/<script\b[^>]*>/gi)) {
    if (!/\btype=["']module["']/i.test(tag)) continue;
    const src = /\bsrc=["']([^"']+)["']/i.exec(tag)?.[1];
    if (!src) continue;
    // Hashed assets get a NEW url in every build, so a cached copy can never be
    // the wrong one — re-downloading the whole bundle here would only make the
    // click slow. Cross-origin entries are not ours to re-prime.
    if (src.includes('/assets/') || /^(?:https?:)?\/\//i.test(src)) continue;
    entries.add(new URL(src, window.location.href).href);
  }

  await Promise.all([...entries].map((url) => fetch(url, { cache: 'reload' })));
}

export function AppUpdateBanner() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [applying, setApplying] = useState(false);
  const detectedRef = useRef(false);
  const location = useLocation();

  async function checkVersion() {
    if (detectedRef.current) return; // already detected — skip
    try {
      const res = await fetch('/version.json', { cache: 'no-store' });
      if (!res.ok) return;
      const data = (await res.json()) as { buildTime: number };
      if (data.buildTime > ((globalThis as { __BUILD_TIME__?: number }).__BUILD_TIME__ ?? 0)) {
        detectedRef.current = true;
        setUpdateAvailable(true);
      }
    } catch {
      // Network error — silently ignore
    }
  }

  // Trigger on every route change (covers active navigating users)
  useEffect(() => {
    void checkVersion();
  }, [location.pathname]);

  // Trigger when user returns to this tab (covers tab-switching users)
  useEffect(() => {
    function handleVisibility() {
      if (document.visibilityState === 'visible') {
        void checkVersion();
      }
    }
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  const applyUpdate = useCallback(async () => {
    setApplying(true);
    try {
      await Promise.race([primeBootChain(), sleep(PRIME_TIMEOUT_MS)]);
    } catch {
      // Offline, blocked, or a malformed document — reloading is still the best
      // move available, and it is exactly what this button did before.
    }
    window.location.reload();
  }, []);

  if (!updateAvailable) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="border-background/15 bg-foreground text-background fixed bottom-4 left-1/2 z-[9999] flex w-[calc(100vw-2rem)] max-w-[30rem] -translate-x-1/2 items-center gap-3 rounded-xl border px-4 py-3 shadow-lg"
    >
      <RefreshCw
        className={cn('text-primary h-4 w-4 shrink-0', applying && 'motion-safe:animate-spin')}
        strokeWidth={1.5}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <p className="text-xl font-medium">New version available</p>
        <p className="text-background/70 text-lg">
          {applying ? 'Loading the latest build…' : 'Refresh to get the latest updates'}
        </p>
      </div>
      <Button
        size="xs"
        className="h-7 shrink-0 px-3 text-lg"
        disabled={applying}
        onClick={() => void applyUpdate()}
      >
        {applying ? 'Updating…' : 'Update Now'}
      </Button>
    </div>
  );
}
